import { Col, Row, Select, Table, Typography } from "antd";
import { Form } from "antd";
import moment from "moment";
import { fetchTopSpotifyGenre } from "radar/Service/RadarService";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Charts } from "shared/components";
import {
  RADAR_TOP_SPOTIFY_FILTER,
  RADAR_TOP_SPOTIFY_GENRES,
} from "shared/constants/LocalStorageConsts";
import AudioFeaturesTrend from "./audio-fetures-trend/AudioFeturesTrend";

const defaults = {
  defaultFilter: {},
  defaultForm: { monthQuantity: "", size: "" },
  defaultGenres: [],
};
const initial = () => {
  const savedFilter = localStorage.getItem(RADAR_TOP_SPOTIFY_FILTER);
  const selectedGenres = localStorage.getItem(RADAR_TOP_SPOTIFY_GENRES);

  if (savedFilter) {
    const parsed = JSON.parse(savedFilter);
    const genres = JSON.parse(selectedGenres || "[]");
    return {
      defaultFilter: parsed,
      defaultGenres: genres,
    };
  }
  return {
    defaultGenres: [],
  };
};

const TopSpotify: React.FC<any> = () => {
  // const { history } = props;
  const { defaultFilter, defaultGenres } = initial();
  const [topGenres, setTopGenres] = useState<any>();
  const [selectedRowKeys, setSelectedRowKeys] = useState(defaultGenres);
  const [filter, setFilter] = useState(defaultFilter);
  const [form] = Form.useForm();

  const { t } = useTranslation("radar");
  const [graph, setGraph] = useState({ series: [], categories: [] });

  const periodOptions = [
    // { label: t("pages.TopSpotify.periodMonth", { month: 1 }), value: 1 },
    // { label: t("pages.TopSpotify.periodMonths", { months: 3 }), value: 3 },
    // { label: t("pages.TopSpotify.periodMonths", { months: 6 }), value: 6 },
    // { label: t("pages.TopSpotify.periodYear", { year: 1 }), value: 12 },
    { label: t("pages.TopSpotify.totalPeriod"), value: 0 },
  ];

  const topGenresOptions = [
    { label: t("pages.TopSpotify.topGenre", { top: 200 }), value: 200 },
    { label: t("pages.TopSpotify.topGenre", { top: 100 }), value: 100 },
    { label: t("pages.TopSpotify.topGenre", { top: 50 }), value: 50 },
    { label: t("pages.TopSpotify.topGenre", { top: 10 }), value: 10 },
  ];

  const columns = [
    {
      title: t("pages.TopSpotify.position"),
      dataIndex: "position",
    },
    {
      title: t("pages.TopSpotify.name"),
      dataIndex: "name",
      render: (value: string) => <>{getNoGenre(value)}</>,
    },
    {
      title: t("pages.TopSpotify.count"),
      dataIndex: "count",
    },
    {
      title: t("pages.TopSpotify.rate"),
      dataIndex: "rate",
      render: (value: any) => <>{`${value} %`}</>,
    },
  ];

  const onSelectChange = (selectedKeys: any) => {
    if (selectedKeys.length > 5) {
      const selectedGenre = selectedKeys.slice(1);
      localStorage.setItem(
        RADAR_TOP_SPOTIFY_GENRES,
        JSON.stringify(selectedGenre)
      );
      setSelectedRowKeys(selectedGenre);
    } else {
      localStorage.setItem(
        RADAR_TOP_SPOTIFY_GENRES,
        JSON.stringify(selectedKeys)
      );
      setSelectedRowKeys(selectedKeys);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    hideSelectAll: true,
  };

  const onDeleteClick = () => {
    form.setFieldsValue(defaults.defaultForm);
    setFilter(defaults.defaultFilter);
    localStorage.setItem(
      RADAR_TOP_SPOTIFY_FILTER,
      JSON.stringify(defaults.defaultFilter)
    );
    localStorage.setItem(
      RADAR_TOP_SPOTIFY_GENRES,
      JSON.stringify(defaults.defaultGenres)
    );
    setTopGenres(undefined);
    setGraph({
      categories: [],
      series: [],
    });
  };

  const onFinishFilter = () => {
    const formFilter = form.getFieldsValue(true);
    localStorage.setItem(RADAR_TOP_SPOTIFY_FILTER, JSON.stringify(formFilter));
    setFilter({ ...formFilter });
  };

  const getNoGenre = useCallback(
    (genre: string) =>
      genre === "no_genre" ? t("pages.TopSpotify.noGenre") : genre,
    [t]
  );

  useEffect(() => {
    const getTopGenres = () => {
      if (topGenres) {
        const reduced = topGenres.data.reduce(
          (prev: any, curr: any) => {
            prev.categories.push(moment(curr.date).format("MMM DD, YYYY"));
            selectedRowKeys.forEach((genre: any = "") => {
              const dt = prev.series.find(
                (gr: any) => gr.name === getNoGenre(genre)
              );
              if (dt)
                dt.data.push(
                  curr.genresSpotify[genre === "no_genre" ? "noGenre" : genre]
                    ?.count || null
                );
              else {
                prev.series.push({
                  name: getNoGenre(genre),
                  data: [
                    curr.genresSpotify[genre === "no_genre" ? "noGenre" : genre]
                      ?.count || null,
                  ],
                });
              }
            });
            return prev;
          },
          {
            series: [] as any,
            categories: [] as any,
          }
        );

        setGraph(reduced);
      } else
        setGraph({
          categories: [],
          series: [],
        });
    };

    getTopGenres();
  }, [topGenres, selectedRowKeys, getNoGenre]);

  useEffect(() => {
    const getTopSpotify = async () => {
      const { monthQuantity, size } = filter;
      if ((monthQuantity || monthQuantity === 0) && size) {
        setSelectedRowKeys([]);
        const response: any = await fetchTopSpotifyGenre(monthQuantity, size);
        setTopGenres(response);
        setSelectedRowKeys(
          response.genreSpotifyRank
            .filter((g: any, i: number) => i < 3)
            .map(({ name }: any) => name)
        );
      }
    };

    if (filter) getTopSpotify();
  }, [filter]);

  return (
    <div className="radar-home">
      <Row>
        <Col xs={24}>
          <Form
            form={form}
            initialValues={filter}
            onFinish={onFinishFilter}
            className="filtro"
            layout="vertical"
          >
            <Card style={{ minHeight: 100 }}>
              <Row gutter={16} justify="center">
                <Col xs={6}>
                  <Form.Item
                    name="monthQuantity"
                    label={t("pages.TopSpotify.period")}
                  >
                    <Select
                      options={periodOptions}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={6}>
                  <Form.Item
                    name="size"
                    label={t("pages.TopSpotify.topGenreLabel")}
                  >
                    <Select
                      options={topGenresOptions}
                      allowClear
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={3}>
                  <div className="filter-button ">
                    <span className="delete" onClick={onDeleteClick}>
                      {t("pages.ArtistsRanking.delete")}
                    </span>
                    <button className="apply">
                      {t("pages.ArtistsRanking.filter")}
                    </button>
                  </div>
                </Col>
              </Row>
            </Card>
          </Form>
          {/* <Row style={{ marginTop: "16px" }}>
            <Col style={{ textAlign: "center" }}>
              <Radio.Group value="trends">
                <Radio.Button
                  value="trends"
                  onClick={() => history.push("/trends")}
                >
                  {t("pages.AudioFeaturesTrend.trends")}
                </Radio.Button>
                <Radio.Button
                  value="genresAudiofeaturesTrend"
                  onClick={() => history.push("/genres-audio-features-trend")}
                >
                  {t("pages.AudioFeaturesTrend.genreAudioFeatureTrend")}
                </Radio.Button>
              </Radio.Group>
            </Col>
          </Row> */}
          <Row gutter={[16, 16]}>
            <Col xs={24} hidden={!topGenres}>
              <Card>
                <Typography.Title
                  level={3}
                  style={{ color: "white", margin: 16 }}
                >
                  {t("pages.TopSpotify.genreLabel")}
                </Typography.Title>
                <Table
                  rowKey={(row: any) => row.name}
                  rowSelection={rowSelection}
                  columns={columns}
                  dataSource={topGenres?.genreSpotifyRank || []}
                  pagination={{
                    pageSize: 5,
                    showSizeChanger: false,
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} hidden={!topGenres}>
              <Card>
                <Charts.LineChart
                  titulo={t("pages.TopSpotify.chartLabel")}
                  {...graph}
                  xaxisLabelFormat="MM/yy"
                  xaxisType="datetime"
                  height={500}
                />
              </Card>
            </Col>
          </Row>
        </Col>
        <AudioFeaturesTrend filter={filter} />
      </Row>
    </div>
  );
};

export default TopSpotify;
